import React, { useEffect, useMemo, useState } from "react";
import { api, apiError, API_BASE } from "../api/client";
import { useI18n, formatDateTime, formatDate, formatMoney } from "../i18n";
import { useAuth } from "../contexts/AuthContext";
import { PageHeader, Card, Badge, EmptyState } from "../components/shared";
import {
  Download, FileText, Wallet, PiggyBank, Package, Stethoscope, Users as UsersIcon,
  FileSpreadsheet, Filter, X, BarChart3,
} from "lucide-react";
import {
  ResponsiveContainer, BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip,
  CartesianGrid, PieChart, Pie, Cell, Legend,
} from "recharts";
import { toast } from "sonner";

const INPUT = "w-full rounded-xl border border-[var(--border-default)] bg-white px-4 h-12 text-base focus:border-brand focus:ring-2 focus:ring-brand/20 outline-none";

const KINDS = [
  { key: "invoices", label: "Factures", icon: FileText, tone: "brand" },
  { key: "payments", label: "Paiements", icon: Wallet, tone: "success" },
  { key: "cash", label: "Caisse", icon: PiggyBank, tone: "warning" },
  { key: "stock", label: "Stock", icon: Package, tone: "terracotta" },
  { key: "consultations", label: "Consultations", icon: Stethoscope, tone: "brand" },
  { key: "patients", label: "Patients", icon: UsersIcon, tone: "success" },
];

const METHOD_LABEL = { cash: "Espèces", card: "Carte", wave: "Wave", orange_money: "Orange Money" };
const PIE_COLORS = ["#086950", "#2d8a56", "#2563eb", "#e3a03b", "#b04a34", "#8b3a8a"];

export default function Reports() {
  const { t } = useI18n();
  const { tenant } = useAuth();
  const cur = tenant?.currency || "XOF";

  const today = new Date().toISOString().slice(0, 10);
  const monthAgo = new Date(Date.now() - 30 * 86400000).toISOString().slice(0, 10);

  const [kind, setKind] = useState("invoices");
  const [from, setFrom] = useState(monthAgo);
  const [to, setTo] = useState(today);
  const [doctorId, setDoctorId] = useState("all");
  const [userId, setUserId] = useState("all");
  const [method, setMethod] = useState("all");
  const [doctors, setDoctors] = useState([]);
  const [users, setUsers] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get("/users");
        setDoctors(res.data.filter((u) => u.role === "doctor"));
        setUsers(res.data.filter((u) => ["cashier", "secretary", "owner", "admin"].includes(u.role)));
      } catch {}
    })();
  }, []);

  const run = async () => {
    setLoading(true);
    try {
      const params = {};
      if (from) params.from = from;
      if (to) params.to = to;
      if (kind === "consultations" && doctorId !== "all") params.doctor_id = doctorId;
      if ((kind === "payments" || kind === "cash" || kind === "stock") && userId !== "all") params.user_id = userId;
      if (kind === "payments" && method !== "all") params.method = method;
      const { data } = await api.get(`/reports/${kind}/preview`, { params });
      setData(data);
    } catch (err) {
      toast.error(apiError(err, t("common.error")));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { run(); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind]);

  const exportFile = async (fmt) => {
    setExporting(fmt);
    try {
      const qs = new URLSearchParams({ format: fmt });
      if (from) qs.set("from", from);
      if (to) qs.set("to", to);
      if (kind === "consultations" && doctorId !== "all") qs.set("doctor_id", doctorId);
      if ((kind === "payments" || kind === "cash" || kind === "stock") && userId !== "all") qs.set("user_id", userId);
      if (kind === "payments" && method !== "all") qs.set("method", method);
      const token = localStorage.getItem("mediflow_token");
      const res = await fetch(`${API_BASE}/reports/${kind}?${qs}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Export failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${kind}_${today}.${fmt}`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success(t("common.success"));
    } catch (err) {
      toast.error(apiError(err, t("common.error")));
    } finally {
      setExporting("");
    }
  };

  return (
    <div>
      <PageHeader
        title={<span className="inline-flex items-center gap-2"><BarChart3 className="w-6 h-6 text-brand" /> {t("reports.title")}</span>}
        actions={
          <div className="flex gap-2">
            <button onClick={() => exportFile("xlsx")} disabled={!data || exporting} data-testid="rep-xlsx"
              className="h-12 px-4 rounded-xl border border-[var(--border-default)] bg-white hover:bg-subtle text-sm font-semibold flex items-center gap-2 disabled:opacity-50">
              <FileSpreadsheet className="w-4 h-4 text-[#2d8a56]" /> Excel
            </button>
            <button onClick={() => exportFile("pdf")} disabled={!data || exporting} data-testid="rep-pdf"
              className="h-12 px-4 rounded-xl border border-[var(--border-default)] bg-white hover:bg-subtle text-sm font-semibold flex items-center gap-2 disabled:opacity-50">
              <FileText className="w-4 h-4 text-[#b12e31]" /> PDF
            </button>
          </div>
        }
      />

      {/* Kind tabs */}
      <div className="mb-4 overflow-x-auto">
        <div className="flex gap-2 p-1 rounded-2xl bg-subtle w-fit" data-testid="rep-tabs">
          {KINDS.map((k) => (
            <button key={k.key} onClick={() => setKind(k.key)} data-testid={`tab-${k.key}`}
              className={`h-11 px-4 rounded-xl font-semibold flex items-center gap-2 transition-colors ${kind === k.key ? "bg-white shadow-sm text-brand" : "text-ink-muted hover:text-ink"}`}>
              <k.icon className="w-4 h-4" /> {k.label}
            </button>
          ))}
        </div>
      </div>

      {/* Filters */}
      <Card className="p-4 mb-5">
        <div className="flex items-center gap-2 mb-3">
          <Filter className="w-4 h-4 text-brand" />
          <div className="font-heading font-bold text-ink">Filtres</div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3">
          <div>
            <label className="text-xs uppercase font-semibold text-ink-muted mb-1 block">Du</label>
            <input type="date" className={INPUT} value={from} onChange={(e) => setFrom(e.target.value)} data-testid="rep-from" />
          </div>
          <div>
            <label className="text-xs uppercase font-semibold text-ink-muted mb-1 block">Au</label>
            <input type="date" className={INPUT} value={to} onChange={(e) => setTo(e.target.value)} data-testid="rep-to" />
          </div>
          {kind === "consultations" && (
            <div>
              <label className="text-xs uppercase font-semibold text-ink-muted mb-1 block">Médecin</label>
              <select className={INPUT} value={doctorId} onChange={(e) => setDoctorId(e.target.value)} data-testid="rep-doctor">
                <option value="all">Tous</option>
                {doctors.map((d) => <option key={d.id} value={d.id}>{d.first_name} {d.last_name}</option>)}
              </select>
            </div>
          )}
          {(kind === "payments" || kind === "cash" || kind === "stock") && (
            <div>
              <label className="text-xs uppercase font-semibold text-ink-muted mb-1 block">Utilisateur</label>
              <select className={INPUT} value={userId} onChange={(e) => setUserId(e.target.value)} data-testid="rep-user">
                <option value="all">Tous</option>
                {users.map((u) => <option key={u.id} value={u.id}>{u.first_name} {u.last_name}</option>)}
              </select>
            </div>
          )}
          {kind === "payments" && (
            <div>
              <label className="text-xs uppercase font-semibold text-ink-muted mb-1 block">Méthode</label>
              <select className={INPUT} value={method} onChange={(e) => setMethod(e.target.value)} data-testid="rep-method">
                <option value="all">Toutes</option>
                {Object.entries(METHOD_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
          )}
          <div className="col-span-2 lg:col-span-2 flex items-end">
            <button onClick={run} disabled={loading} data-testid="rep-run"
              className="w-full h-12 rounded-xl bg-brand hover:bg-brand-hover text-white font-heading font-bold disabled:opacity-60">
              {loading ? t("common.loading") : "Générer l'aperçu"}
            </button>
          </div>
        </div>
      </Card>

      {loading && !data ? (
        <Card className="py-16 text-center text-ink-muted">{t("common.loading")}</Card>
      ) : !data ? null : (
        <>
          <SummaryRow kind={kind} summary={data.summary} cur={cur} />
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mt-5 mb-5">
            <div className="lg:col-span-2">
              <ChartPanel kind={kind} chart={data.chart_data} cur={cur} />
            </div>
            <InsightsPanel kind={kind} insights={data.insights} cur={cur} />
          </div>
          <PreviewTable kind={kind} rows={data.rows} cur={cur} />
        </>
      )}
    </div>
  );
}

// ---------- Summary row ----------

function SummaryRow({ kind, summary, cur }) {
  const items = (() => {
    if (kind === "invoices") return [
      { label: "Total facturé", value: formatMoney(summary.total_invoiced || 0, cur), tone: "brand" },
      { label: "Total encaissé", value: formatMoney(summary.total_collected || 0, cur), tone: "success" },
      { label: "Reste impayé", value: formatMoney(summary.outstanding || 0, cur), tone: (summary.outstanding || 0) > 0 ? "danger" : "neutral" },
      { label: "Nombre de factures", value: summary.count || 0, tone: "neutral" },
    ];
    if (kind === "payments") return [
      { label: "Total encaissé", value: formatMoney(summary.total || 0, cur), tone: "success" },
      { label: "Nombre de paiements", value: summary.count || 0, tone: "neutral" },
      { label: "Paiement moyen", value: formatMoney(summary.avg || 0, cur), tone: "brand" },
    ];
    if (kind === "cash") return [
      { label: "Sessions", value: summary.sessions || 0, tone: "neutral" },
      { label: "Total ouvert", value: formatMoney(summary.opening_total || 0, cur), tone: "neutral" },
      { label: "Total clôturé", value: formatMoney(summary.closing_total || 0, cur), tone: "success" },
      { label: "Écart absolu cumulé", value: formatMoney(summary.variance_abs_total || 0, cur), tone: (summary.variance_abs_total || 0) > 0 ? "warning" : "success" },
      { label: "Sessions avec écart", value: summary.sessions_with_variance || 0, tone: (summary.sessions_with_variance || 0) > 0 ? "warning" : "neutral" },
    ];
    if (kind === "stock") return [
      { label: "Mouvements", value: summary.movements || 0, tone: "neutral" },
      { label: "Entrées", value: summary.in_qty || 0, tone: "success" },
      { label: "Sorties", value: summary.out_qty || 0, tone: "terracotta" },
      { label: "Solde", value: summary.net || 0, tone: (summary.net || 0) >= 0 ? "success" : "danger" },
    ];
    if (kind === "consultations") return [
      { label: "Consultations", value: summary.count || 0, tone: "brand" },
      { label: "Patients uniques", value: summary.unique_patients || 0, tone: "neutral" },
      { label: "Praticiens", value: summary.unique_doctors || 0, tone: "neutral" },
    ];
    if (kind === "patients") return [
      { label: "Nouveaux patients", value: summary.new_patients || 0, tone: "brand" },
    ];
    return [];
  })();
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-5 gap-3" data-testid="rep-summary">
      {items.map((it, i) => (
        <Card key={i} className="p-4" data-testid={`rep-sum-${i}`}>
          <div className="text-xs uppercase tracking-wider text-ink-muted">{it.label}</div>
          <div className={`stat-number text-xl font-heading font-extrabold ${
            it.tone === "success" ? "text-[#2d8a56]" :
            it.tone === "danger" ? "text-[#b12e31]" :
            it.tone === "warning" ? "text-[#8a5a10]" :
            it.tone === "terracotta" ? "text-terracotta" :
            it.tone === "brand" ? "text-brand" : "text-ink"
          }`}>
            {it.value}
          </div>
        </Card>
      ))}
    </div>
  );
}

// ---------- Chart ----------

function ChartPanel({ kind, chart, cur }) {
  if (!chart || !chart.series?.length) {
    return (
      <Card className="p-5" data-testid="rep-chart">
        <div className="font-heading font-bold text-ink mb-2">{chart?.label || "Évolution"}</div>
        <EmptyState icon={BarChart3} title="Aucune donnée graphique" />
      </Card>
    );
  }
  const isStockDual = kind === "stock";
  const isCount = ["consultations", "patients", "stock"].includes(kind);
  return (
    <Card className="p-5" data-testid="rep-chart">
      <div className="font-heading font-bold text-ink mb-2">{chart.label}</div>
      <div className="h-64 w-full">
        <ResponsiveContainer width="100%" height="100%">
          {isStockDual ? (
            <BarChart data={chart.series}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E7E3D8" />
              <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={(v) => v?.slice(5)} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip labelFormatter={(l) => formatDate(l)} contentStyle={{ borderRadius: 12, border: "1px solid #E7E3D8" }} />
              <Legend />
              <Bar dataKey="in" name="Entrées" fill="#2d8a56" />
              <Bar dataKey="out" name="Sorties" fill="#b04a34" />
            </BarChart>
          ) : (
            <LineChart data={chart.series}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E7E3D8" />
              <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={(v) => v?.slice(5)} />
              <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => isCount ? v : `${Math.round(v / 1000)}k`} />
              <Tooltip formatter={(v) => isCount ? v : formatMoney(v, cur)} labelFormatter={(l) => formatDate(l)} contentStyle={{ borderRadius: 12, border: "1px solid #E7E3D8" }} />
              <Line type="monotone" dataKey="value" stroke="#086950" strokeWidth={2.5} dot={{ r: 3 }} />
            </LineChart>
          )}
        </ResponsiveContainer>
      </div>
    </Card>
  );
}

// ---------- Insights panel ----------

function InsightsPanel({ kind, insights, cur }) {
  return (
    <Card className="p-5" data-testid="rep-insights">
      <div className="font-heading font-bold text-ink mb-3">Insights</div>
      {kind === "invoices" && (
        <div className="space-y-3">
          <div>
            <div className="text-xs text-ink-muted uppercase mb-1">Par statut</div>
            {Object.entries(insights.by_status || {}).map(([s, c]) => (
              <div key={s} className="flex items-center justify-between text-sm py-1">
                <Badge tone={s === "paid" ? "success" : s === "partial" ? "warning" : "danger"}>{s}</Badge>
                <span className="stat-number font-bold">{c}</span>
              </div>
            ))}
          </div>
          <div>
            <div className="text-xs text-ink-muted uppercase mb-1 mt-2">Âge des impayés</div>
            {Object.entries(insights.aging || {}).map(([k, v]) => (
              <div key={k} className="flex items-center justify-between text-sm py-1">
                <span className="text-ink">{k}</span>
                <span className={`stat-number font-bold ${v > 0 ? (k === "60j+" ? "text-[#b12e31]" : k === "31-60j" ? "text-[#8a5a10]" : "text-ink") : "text-ink-muted"}`}>
                  {formatMoney(v, cur)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
      {kind === "payments" && (
        <div className="space-y-3">
          <div className="h-40 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={Object.entries(insights.by_method || {}).map(([k, v]) => ({ name: METHOD_LABEL[k] || k, value: v }))}
                  dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={56} innerRadius={32} label={false}>
                  {Object.keys(insights.by_method || {}).map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                </Pie>
                <Tooltip formatter={(v) => formatMoney(v, cur)} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
          {(insights.by_user || []).length > 0 && (
            <div>
              <div className="text-xs text-ink-muted uppercase mb-1 mt-2">Top caissier</div>
              {insights.by_user.slice(0, 5).map((u) => (
                <div key={u.user_id} className="flex items-center justify-between text-sm py-1">
                  <span>{u.name}</span>
                  <span className="stat-number font-bold">{formatMoney(u.amount, cur)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      {kind === "cash" && (
        <div className="space-y-2">
          <div className="text-sm text-ink">Écart net cumulé : <span className={`stat-number font-bold ${(insights.net_variance||0) !== 0 ? "text-[#8a5a10]" : "text-ink-muted"}`}>{formatMoney(insights.net_variance || 0, cur)}</span></div>
          {(insights.variance_by_user || []).length > 0 ? (
            <>
              <div className="text-xs text-ink-muted uppercase mt-2">Écarts par caissier</div>
              {insights.variance_by_user.map((u) => (
                <div key={u.user_id} className="flex items-center justify-between text-sm py-1">
                  <span>{u.name}</span>
                  <span className="stat-number font-bold text-[#8a5a10]">{formatMoney(u.variance_abs, cur)}</span>
                </div>
              ))}
            </>
          ) : <div className="text-sm text-ink-muted">Aucun écart détecté 👌</div>}
        </div>
      )}
      {kind === "stock" && (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <div className="p-3 rounded-xl bg-[#e3a03b]/10">
              <div className="text-xs text-ink-muted uppercase">Stock bas (actuel)</div>
              <div className="stat-number text-2xl font-bold text-[#8a5a10]">{insights.low_stock_current || 0}</div>
            </div>
            <div className="p-3 rounded-xl bg-[#d34044]/10">
              <div className="text-xs text-ink-muted uppercase">Rupture (actuel)</div>
              <div className="stat-number text-2xl font-bold text-[#b12e31]">{insights.out_of_stock_current || 0}</div>
            </div>
          </div>
          {(insights.top_consumed || []).length > 0 && (
            <div>
              <div className="text-xs text-ink-muted uppercase mb-1">Top consommés</div>
              {insights.top_consumed.map((p) => (
                <div key={p.product_id} className="flex items-center justify-between text-sm py-1">
                  <span className="truncate">{p.name}</span>
                  <span className="stat-number font-bold">{p.qty}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      {kind === "consultations" && (
        <div>
          <div className="text-xs text-ink-muted uppercase mb-1">Par praticien</div>
          {(insights.by_doctor || []).length === 0 ? <div className="text-sm text-ink-muted">—</div> :
            insights.by_doctor.map((d) => (
              <div key={d.doctor_id} className="flex items-center justify-between text-sm py-1">
                <span>{d.name}</span>
                <span className="stat-number font-bold">{d.count}</span>
              </div>
            ))
          }
        </div>
      )}
      {kind === "patients" && (
        <div>
          <div className="text-xs text-ink-muted uppercase mb-1">Par genre</div>
          {Object.entries(insights.by_gender || {}).map(([g, c]) => (
            <div key={g} className="flex items-center justify-between text-sm py-1">
              <span className="capitalize">{g}</span>
              <span className="stat-number font-bold">{c}</span>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

// ---------- Preview table ----------

function PreviewTable({ kind, rows, cur }) {
  const [page, setPage] = useState(0);
  const pageSize = 25;
  const pages = Math.max(1, Math.ceil((rows?.length || 0) / pageSize));
  const slice = useMemo(() => rows.slice(page * pageSize, (page + 1) * pageSize), [rows, page]);
  if (!rows || rows.length === 0) {
    return <Card className="p-10"><EmptyState icon={FileText} title="Aucune donnée sur la période" /></Card>;
  }
  const headers = Object.keys(rows[0]);
  const moneyCols = new Set(["total", "paid", "balance", "amount", "opening", "closing", "variance", "amount_paid"]);
  const dateCols = new Set(["date", "opened_at", "closed_at", "created_at"]);
  return (
    <Card className="overflow-hidden" data-testid="rep-table">
      <div className="flex items-center gap-2 p-4 border-b border-[var(--border-default)]">
        <div className="font-heading font-bold text-ink">Aperçu — {rows.length} ligne(s)</div>
        {pages > 1 && (
          <div className="ml-auto flex items-center gap-2">
            <button disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))} className="px-3 py-1 rounded-lg bg-subtle text-sm disabled:opacity-50">‹</button>
            <span className="text-sm text-ink-muted">{page + 1} / {pages}</span>
            <button disabled={page >= pages - 1} onClick={() => setPage((p) => Math.min(pages - 1, p + 1))} className="px-3 py-1 rounded-lg bg-subtle text-sm disabled:opacity-50">›</button>
          </div>
        )}
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-subtle text-xs uppercase text-ink-muted tracking-wider">
              {headers.map((h) => (
                <th key={h} className={`py-2 px-4 ${moneyCols.has(h) ? "text-right" : "text-left"}`}>
                  {h.replace(/_/g, " ")}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {slice.map((r, i) => (
              <tr key={i} className="border-t border-[var(--border-default)] hover:bg-subtle/50">
                {headers.map((h) => (
                  <td key={h} className={`py-2 px-4 ${moneyCols.has(h) ? "text-right stat-number" : ""}`}>
                    {renderCell(r[h], h, cur, moneyCols, dateCols)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

function renderCell(v, key, cur, moneyCols, dateCols) {
  if (v == null || v === "") return <span className="text-ink-muted">—</span>;
  if (moneyCols.has(key) && typeof v === "number") return formatMoney(v, cur);
  if (dateCols.has(key) && typeof v === "string" && v.includes("T")) return formatDateTime(v);
  if (key === "method" && METHOD_LABEL[v]) return METHOD_LABEL[v];
  if (key === "status") {
    const tone = v === "paid" || v === "completed" || v === "closed" ? "success" : v === "partial" || v === "open" ? "warning" : v === "unpaid" || v === "failed" ? "danger" : "neutral";
    return <Badge tone={tone}>{v}</Badge>;
  }
  return String(v);
}
